// src/components/TestimonialCard.tsx
import { Quote, Star, CheckCircle, Calendar, MapPin } from 'lucide-react';
import { motion } from 'framer-motion';

interface TestimonialCardProps {
  quote: string;
  author: string;
  role: string;
  image?: string;
  rating?: number;
  date?: string;
  location?: string;
  verified?: boolean;
  featured?: boolean;
  delay?: number;
}

export const TestimonialCard = ({ 
  quote, 
  author, 
  role, 
  image, 
  rating = 5,
  date,
  location,
  verified = false,
  featured = false,
  delay = 0 
}: TestimonialCardProps) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6, delay }}
      whileHover={{ y: -8, scale: 1.02 }}
      className={`bg-card border-2 ${featured ? 'border-yellow-500/50' : 'border-border'} rounded-2xl p-8 card-hover relative overflow-hidden group`}
    >
      {/* Featured Badge */}
      {featured && (
        <div className="absolute top-4 right-4">
          <div className="flex items-center gap-1 px-3 py-1 rounded-full bg-yellow-500/20 border border-yellow-500/30">
            <Star className="h-3 w-3 text-yellow-500 fill-yellow-500" />
            <span className="text-xs font-bold text-yellow-500">FEATURED</span>
          </div>
        </div>
      )}

      {/* Background Gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-accent/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

      {/* Content */}
      <div className="relative">
        {/* Quote Icon */}
        <motion.div
          animate={{ 
            scale: [1, 1.1, 1],
            rotate: [0, 5, 0]
          }}
          transition={{ duration: 3, repeat: Infinity }}
        >
          <Quote className="h-12 w-12 text-primary mb-6 opacity-30" />
        </motion.div>

        {/* Rating Stars */}
        <div className="flex items-center gap-1 mb-4">
          {[...Array(5)].map((_, i) => (
            <Star
              key={i}
              className={`h-4 w-4 ${
                i < rating 
                  ? 'text-yellow-500 fill-yellow-500' 
                  : 'text-muted-foreground/30'
              }`}
            />
          ))}
        </div>

        {/* Quote Text */}
        <p className="text-muted-foreground mb-6 leading-relaxed text-base italic">
          "{quote}"
        </p>

        {/* Meta Info */}
        <div className="space-y-3 mb-6">
          {date && (
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Calendar className="h-3 w-3" />
              <span>{new Date(date).toLocaleDateString()}</span>
            </div>
          )}
          {location && (
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <MapPin className="h-3 w-3" />
              <span>{location}</span>
            </div>
          )}
        </div>

        {/* Author Section */}
        <div className="flex items-center justify-between pt-6 border-t border-border">
          <div className="flex items-center gap-4">
            {image ? (
              <div className="relative">
                <img
                  src={image}
                  alt={author}
                  className="h-14 w-14 rounded-full object-cover border-2 border-primary/30 group-hover:border-primary transition-colors"
                />
                {verified && (
                  <div className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-green-500 border-2 border-card flex items-center justify-center">
                    <CheckCircle className="h-3 w-3 text-white" />
                  </div>
                )}
              </div>
            ) : (
              <div className="h-14 w-14 rounded-full bg-primary/20 flex items-center justify-center text-primary font-bold text-xl border-2 border-primary/30">
                {author[0]}
              </div>
            )}
            <div>
              <div className="font-bold text-foreground flex items-center gap-2">
                {author}
                {verified && !image && (
                  <CheckCircle className="h-4 w-4 text-green-500" />
                )}
              </div>
              <div className="text-sm text-muted-foreground">{role}</div>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};
